<template>
    <v-menu v-model="spaceMenu" offset-x :close-on-content-click="false" nudge-top="8">
        <template v-slot:activator="{ on: menuOn }">
            <v-tooltip right :disabled="!collapsed">
                <template v-slot:activator="{ on: tooltipOn }">
                    <v-list-item
                        :disabled="fetchingSpaces"
                        v-on="{ ...menuOn, ...tooltipOn }"
                        @mouseenter="isMenuActivatorHovered = true"
                        @mouseleave="isMenuActivatorHovered = false"
                        :input-value="Boolean(activeSpaceSid)"
                        active-class="primary--text">
                        <v-list-item-icon>
                            <v-icon>{{ isMenuActivatorHovered ? 'mdi-chevron-right' : 'mdi-format-list-text' }}</v-icon>
                        </v-list-item-icon>
                        <v-list-item-content v-if="fetchingSpaces">
                            <v-progress-linear indeterminate rounded></v-progress-linear>
                        </v-list-item-content>
                        <v-list-item-content v-else>
                            <v-list-item-title class="d-flex align-center">
                                <b>{{ spaceName }}</b>
                            </v-list-item-title>
                        </v-list-item-content>
                        <v-list-item-action v-if="showQuickActions && !collapsed" class="my-0">
                            <v-menu v-model="spaceActionsMenu" offset-x right nudge-top="10">
                                <template v-slot:activator="{ on, attrs }">
                                    <v-btn text small class="px-2" min-width="0" v-bind="attrs" v-on="on" @click.stop.prevent>
                                        <v-icon small>more_horiz</v-icon>
                                    </v-btn>
                                </template>
                                <v-list dense nav>
                                    <v-list-item @click="goToSpaceSettings()">
                                        <v-list-item-icon>
                                            <v-icon>mdi-cog-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Settings</v-list-item-title>
                                    </v-list-item>
                                    <v-list-item v-if="isSpaceAdmin" @click="goToSpaceUsers()">
                                        <v-list-item-icon>
                                            <v-icon>mdi-account-group-outline</v-icon>
                                        </v-list-item-icon>
                                        <v-list-item-title class="mr-4">Users</v-list-item-title>
                                    </v-list-item>
                                    <SpaceInviteDialog v-if="isSpaceAdmin">
                                        <template v-slot:activator>
                                            <v-list-item @click="spaceActionsMenu = false">
                                                <v-list-item-icon>
                                                    <v-icon>mdi-account-plus-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="mr-4">Invite users</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </SpaceInviteDialog>
                                    <v-divider class="my-2"></v-divider>
                                    <EntityLeaveDialog entity-type="space">
                                        <template v-slot:activator>
                                            <v-list-item @click="spaceActionsMenu = false">
                                                <v-list-item-icon>
                                                    <v-icon color="error">mdi-logout</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="error--text mr-4">Leave space</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </EntityLeaveDialog>
                                    <EntityDeleteDialog
                                        v-if="isSpaceAdmin"
                                        entity-type="space"
                                        :organization-id="activeOrgOid"
                                        :space-id="activeSpaceSid"
                                        :space-name="spaceName">
                                        <template v-slot:activator="{ attrs, open }">
                                            <v-list-item v-bind="attrs" @click.stop="open">
                                                <v-list-item-icon>
                                                    <v-icon color="error">mdi-trash-can-outline</v-icon>
                                                </v-list-item-icon>
                                                <v-list-item-title class="error--text mr-4">Delete space</v-list-item-title>
                                            </v-list-item>
                                        </template>
                                    </EntityDeleteDialog>
                                </v-list>
                            </v-menu>
                        </v-list-item-action>
                    </v-list-item>
                </template>
                <span>
                    <b>{{ spaceName }}</b>
                </span>
            </v-tooltip>
        </template>

        <ContextSelectorList
            level="space"
            :open="spaceMenu"
            :selected-id="activeSpaceSid"
            @select="openSpace($event.sid, $event.iid, $event.snid)"
            @view-all="gotoSpaces" />
    </v-menu>
</template>

<script>
import { mapState, mapGetters } from 'vuex'
import EntityLeaveDialog from '@/components/EntityLeaveDialog.vue'
import ContextSelectorList from '@/components/contextSelectors/ContextSelectorList.vue'

const EntityDeleteDialog = () => import('@/components/EntityDeleteDialog.vue')
const SpaceInviteDialog = () => import('@/modules/space/components/SpaceInviteDialog.vue')

export default {
    name: 'SpaceMenu',
    components: {
        ContextSelectorList,
        EntityDeleteDialog,
        SpaceInviteDialog,
        EntityLeaveDialog
    },
    props: {
        collapsed: {
            type: Boolean,
            default: false
        },
        showQuickActions: {
            type: Boolean,
            default: false
        }
    },
    data() {
        return {
            spaceMenu: false,
            spaceActionsMenu: false,
            isMenuActivatorHovered: false
        }
    },
    computed: {
        ...mapState('orgStore', ['fetchingSpaces']),
        ...mapGetters('spaceStore', ['currentSpaceName', 'isSpaceAdmin']),
        spaceName() {
            if (this.$route.params.sid !== undefined && this.currentSpaceName) {
                return this.currentSpaceName
            }
            return 'Select space'
        },
        activeOrgOid() {
            return this.$route.params.oid
        },
        activeSpaceSid() {
            return this.$route.params.sid
        }
    },
    methods: {
        goToSpaceSettings() {
            this.spaceActionsMenu = false
            if (!this.activeOrgOid || !this.activeSpaceSid) {
                return
            }
            this.$router.push({
                name: 'config-space-general',
                params: { oid: this.activeOrgOid, sid: this.activeSpaceSid }
            })
        },
        goToSpaceUsers() {
            this.spaceActionsMenu = false
            if (!this.activeOrgOid || !this.activeSpaceSid) {
                return
            }
            this.$router.push({
                name: 'config-space-users',
                params: { oid: this.activeOrgOid, sid: this.activeSpaceSid }
            })
        },
        openSpace(sid, iid, snid) {
            this.spaceMenu = false

            if ((this.$route.name || '').startsWith('config')) {
                this.$router.push({
                    name: 'config-space-general',
                    params: { ...this.$route.params, sid },
                    query: { ...this.$route.query }
                })
                return
            }

            this.$router.push({ name: 'snapshot-overview', params: { oid: this.$route.params.oid, sid, iid, snid } })
        },

        gotoSpaces() {
            this.spaceMenu = false
            this.$router.push({ name: 'home-spaces', params: { oid: this.$route.params.oid } })
        }
    }
}
</script>
